import { askCXAssistant } from "features/ai/reducer/ai-actions";
import { TieredMenu } from "primereact/tieredmenu";
import { IconDispatcher } from "shared/Icons";
import "../editor.scss";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useSelector } from "react-redux";
const languages = [
	{ label: "English", code: "en" },
	{ label: "French", code: "fr" },
	{ label: "Spanish", code: "es" },
	{ label: "German", code: "de" },
	{ label: "Dutch", code: "nl" },
	{ label: "Greek", code: "el" },
	{ label: "Italian", code: "it" },
	{ label: "Portuguese", code: "pt" },
	{ label: "Russian", code: "ru" },
	{ label: "Chinese", code: "zh" },
	{ label: "Japanese", code: "ja" },
	{ label: "Korean", code: "ko" },
	{ label: "Arabic", code: "ar" },
	{ label: "Hindi", code: "hi" },
	{ label: "Turkish", code: "tr" },
	{ label: "Swedish", code: "sv" },
	{ label: "Norwegian", code: "no" },
	{ label: "Danish", code: "da" },
];

export const AIOptions = ({ editor, insertRef, handleshow, alexLoading, forceUpdate }) => {
	const { t } = useTranslation();
	const aiAllowed = useSelector(({ settings }) => settings.subscriptionDetails.aiEnabled);

	const typingEffectFunction = (data) => {
		if (!data.result) return;
		let i = 0;
		alexLoading.current.typing = true;
		forceUpdate((f) => !f); // Trigger re-render

		const typingEffect = () => {
			const step = 3; // Number of characters to insert per iteration

			if (i < data.result.length) {
				editor
					.chain()
					.focus()
					.insertContent(data.result.slice(i, i + step))
					.run();
				i += step;
				requestAnimationFrame(typingEffect);
			} else {
				// Only set typing to false after all characters are inserted
				alexLoading.current.typing = false;
				forceUpdate((f) => !f); // Trigger re-render
			}
		};
		requestAnimationFrame(typingEffect);
	};

	const askAI = (prompt) => {
		try {
			alexLoading.current.loading = true;
			forceUpdate((f) => !f); // Trigger re-render

			askCXAssistant({
				prompt,
				max_tokens: 2000,
				onSuccess: (data) => {
					typingEffectFunction(data);
					alexLoading.current.loading = false;
					forceUpdate((f) => !f); // Trigger re-render
				},
				// Adjust the interval time to control typing speed
			});
		} catch (e) {
			console.log(e);
		}
	};
	const handleAIAction = (attribute, event, arg = null) => {
		if (event) {
			event.preventDefault();
		}
		const { from, to } = editor.state.selection;
		const selectedText = editor.state.doc.textBetween(from, to, " ");
		switch (attribute) {
			case "makeShorter": {
				const prompt = `make this content shorter: ${selectedText}. Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists. `;
				askAI(prompt);
				break;
			}
			case "makeLonger": {
				const prompt = `make this content longer: ${selectedText}.  Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			case "changeTone": {
				const prompt = `change the tone of this content: ${selectedText} to ${arg}. Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			case "summarize": {
				const prompt = `Summarize the following content in a single, concise paragraph: ${selectedText}.  Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			case "translate": {
				const prompt = `translate this content to ${arg}: ${selectedText}. Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			case "fixGrammar": {
				const prompt = `fix the grammar of this content: ${selectedText}.  Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			case "paraphrase": {
				const prompt = `paraphrase this content: ${selectedText}.  Return the response in JSON format with a "result" key, and only add text directly in "result" without further nested keys or lists.`;
				askAI(prompt);
				break;
			}
			default:
				break;
		}
		handleshow(event, "ai");
	};

	if (!editor) {
		return null;
	}
	if (!aiAllowed) {
		return null;
	}
	const languageTemplates = languages.map((language) => ({
		template: (
			<div key={language.code} onClick={(e) => handleAIAction("translate", e, language.label)} className={" pointer p-2 px-4"} style={{ minWidth: "200px" }}>
				{language.label}
			</div>
		),
	}));

	const tonesTemplates = ["FORMAL", "INFORMAL", "FRIENDLY", "PROFESSIONAL", "HUMOROUS", "SERIOUS", "OPTIMISTIC", "PESSIMISTIC", "NEUTRAL"].map((tone) => ({
		template: (
			<div key={tone} onClick={(e) => handleAIAction("changeTone", e, tone)} className={" pointer p-2 px-4"} style={{ minWidth: "200px" }}>
				{t(tone)}
			</div>
		),
	}));
	const items = [
		{ label: t("MAKE_SHORTER"), icon: "grip-lines", attribute: "makeShorter" },
		{ label: t("MAKE_LONGER"), icon: "bars-sort", attribute: "makeLonger" },
		{
			label: t("CHANGE_TONE"),
			icon: IconDispatcher("microphone", "mx-1 me-2-6", { family: "sharp" }),
			attribute: "changeTone",
			items: tonesTemplates,
		},
		{ label: t("SUMMARIZE"), icon: "file-lines", text: "Summarize", attribute: "summarize" },
		{
			label: t("TRANSLATE"),
			icon: IconDispatcher("globe", "mx-1 me-2-6", { family: "sharp" }),
			attribute: "translate",
			items: languageTemplates,
		},
		{ label: t("FIX_GRAMMAR"), icon: "spell-check", attribute: "fixGrammar" },
		{ label: t("PARAPHRASE"), icon: "sync-alt", attribute: "paraphrase" },
	];

	const templates = items.map((item) =>
		item.separator
			? item
			: ["translate", "changeTone"].includes(item.attribute)
			? item
			: {
					template: (
						<div key={item.label} onClick={(e) => handleAIAction(item.attribute, e)} className={" pointer p-2 px-4"} style={{ minWidth: "200px" }}>
							{IconDispatcher(item.icon, "me-2", { family: "sharp" })} {item.label}
						</div>
					),
			}
	);

	return (
		<>
			<TieredMenu model={templates} popup ref={insertRef} breakpoint="767px" className="ai-language-popup" />
			<Button
				key={"ai"}
				title={t("REWRITE_WITH_ALEX")}
				className={"d-flex justify-content-center align-items-center"}
				style={{ minWidth: "40px" }}
				onClick={(e) => {
					e.preventDefault();
					handleshow(e, "ai");
				}}>
				<span>{IconDispatcher("SPARKLES", "", { family: "sharp" })}</span>
				<span>{t("REWRITE_WITH_ALEX")}</span>
				<span>{IconDispatcher("angle-down", "ms-1", { size: "sm" })}</span>
			</Button>
		</>
	);
};

const Button = styled.button`
	display: flex;
	align-items: center;
	white-space: nowrap;
	gap: 0.5rem;
`;
